/***
 * Summary: here resides the scheme and roles for the fields of the network performing
 * 
 */
import { yupToFormErrors } from "formik";
import * as Yup from "yup";

const createNumberValidation = (fieldName) =>
    Yup.number()
        .typeError(`Invalid data. '${fieldName}' must be a number`)
        .nullable(true)
        .test('is-number', `Invalid data. '${fieldName}' must be a number`, function (value) {
            return (value === null || value === undefined || !isNaN(value)) || this.createError({ message: `Invalid data. '${fieldName}' must be a number` });
        });

const TODIS_NIWWPORTS_SCHEMA = Yup.object().shape({
    //general section Rules:
    /*   dateValidTo: Yup.date("Valid from is a Date").required().nullable(true),
      dateValidFrom: Yup.date("Valid to is a Date").required().nullable(true), */
    dateValidFor: Yup.string("Valid for is a Year").nullable(true),
    tenTCategory: Yup.string("tent category is alphanumeric").nullable(true),
    nodeNameval: Yup.string("Road ID is alphanumeric").nullable(true),
    nameResp: Yup.string("Road ID is alphanumeric").nullable(true),
    addressResp: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPer: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPos: Yup.string("Road ID is alphanumeric").nullable(true),
    contactNum: Yup.string()
        .transform((originalValue) => {
            // Si el valor comienza con '+', eliminamos ese símbolo
            return typeof originalValue == 'string' ? originalValue.replace(/^\+/, '') : originalValue;
        })
        .nullable(true)
        .test('is-valid', 'Invalid data. \'Phone Number\' must be a valid number', function (value) {
            // Permitir números, espacios, y la presencia de '+'
            const isValid = /^[0-9+\s]+$/.test(value) || value == null;

            if (!isValid) {
                throw this.createError({ message: 'Invalid data. \'Phone Number\' must be a valid number' });
            }
            return true;
        }),

    contactEmail: Yup.string("Email")
        .transform((originalValue) => {
            // Realizar transformaciones si es necesario
            return typeof originalValue == 'string' ? originalValue.trim() : originalValue;
        })
        .nullable(true)
        .test('is-email', 'Invalid data. \'Email\' must be valid', function (value) {
            // Si el valor es null o undefined, lo consideramos válido y no aplicamos más validaciones
            if (value == null || value == undefined) {
                return true;
            }

            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

            // Verificar si es una cadena antes de intentar dividirla
            if (typeof value === 'string') {
                const emails = value.split(/[,;:]+/);  // Separar por comas, punto y coma, o dos puntos

                for (const email of emails) {
                    if (!emailRegex.test(email.trim())) {
                        throw this.createError({ message: `Invalid data. '${email}' must be a valid email address` });
                    }
                }
            }

            return true;
        }),

    countryCode: Yup.string("Country Code").nullable(true),
    ownershipType: Yup.string().nullable(true),
    owner1: Yup.string().nullable(true),
    ownershipType: Yup.string().nullable(true),
    owner1: Yup.string().nullable(true),
    owner1Per: Yup.number()
        .required("Majority Ownership Porcentage is required")
        .min(0, "Main ownership Porcentage must be greater than or equal to 0")
        .max(100, "Main ownership Porcentage must be less than or equal to 100")
        .test('total-percentage', 'Sum of ownership percentages should be 100%', function (value) {
            const owner1Per = parseFloat(this.parent.owner1Per) || 0;
            const ownerOtherPer = parseFloat(this.parent.ownerOtherPer) || 0;
            return owner1Per + ownerOtherPer === 100;
        }),
    ownerOther: Yup.string()
        .test('ownership-format', 'Invalid ownership format', function (value) {
            // Verificar el formato owner;porcentaje;owner;porcentaje
            if (!value) return true; // Permitir valor vacío
            const pairs = value.split(';');
            // Debe haber un número par de elementos
            if (pairs.length % 2 !== 0) return false;
            // Comprobar si cada par tiene un owner y un porcentaje válido
            for (let i = 0; i < pairs.length; i += 2) {
                const owner = pairs[i].trim();
                const percentage = parseFloat(pairs[i + 1].trim());
                if (!owner || isNaN(percentage) || percentage < 0 || percentage > 100) {
                    return false;
                }
            }
            return true;
        })
        .nullable(true),
    ownerOtherPer: Yup.number()
        .when('ownerOther', {
            is: value => value && value.trim().length > 0,
            then: Yup.number()
                .required('The percentage of secondary owners must be filled in if the "Secondary Owners" field is filled in.')
                .test('sum-of-percentages', 'Sum of secondary owners should match', function (value) {
                    const pairs = this.parent.ownerOther ? this.parent.ownerOther.split(';') : [];
                    let totalPercentage = 0;
                    for (let i = 1; i < pairs.length; i += 2) {
                        totalPercentage += parseFloat(pairs[i].trim());
                    }
                    return totalPercentage === value;
                })
        })
        .nullable(true),
    remarks: Yup.string("remarks").nullable(true),

    iwwPortsNpmEnvDTO: Yup.object().shape({
        airPollutGhgTpy: createNumberValidation('TODIS'),
        co2Ems: createNumberValidation('CO2 emissions (tonnes/year)'),
        noxEms: createNumberValidation('NOx emissions (tonnes/year)'),
        so2Ems: createNumberValidation('SO2 emissions (tonnes/year)'),
        nmhcEms: createNumberValidation('Non-methane hydrocarbons (tonnes/year)'),
        ppm: createNumberValidation('Particulate matter (ppm)'),
        noise: createNumberValidation('TODIS'),
        chFloodInci: createNumberValidation('Flooding incidents (no.)'),
        chClosureAdvWeatherCond: createNumberValidation('Closures due to adverse weather conditions (no.)'),
        chEmbankFail: createNumberValidation('Embankment failures (no.)'),
        chWinterMaintDays: createNumberValidation('TODIS'),
        remarks: Yup.string("remarks").nullable(true),
    }),
    iwwPortsNpmInfraDTO: Yup.object().shape({
        activity: Yup.string('Value should be a text').nullable(true),
        condition: Yup.string('Value should be a text').nullable(true),
        totalArea: createNumberValidation('Total land and water area (m2)'),
        openStorage: createNumberValidation('Open storage (m2)'),
        coveredStorage: createNumberValidation('Covered storage (m2)'),
        coldStorage: createNumberValidation('Cold storage (m2)'),
        dangerGoodsStorage: createNumberValidation('Storage of dangerous goods (m2)'),
        handlingEquip: Yup.string('Value should be a text').nullable(true),
        quayLength: createNumberValidation('Qualy length (m)'),
        berths: createNumberValidation('Berths (no.)'),
        maxDraught: createNumberValidation('Maximum draught – natural or dredged (m)'),
        portTerminals: createNumberValidation('Port terminals (ha)'),
        combinedTerminals: createNumberValidation('Combined terminals (ha)'),
        passengerTerminals: createNumberValidation('Passenger terminals (m2)'),
        passengerCapacity: createNumberValidation('Passenger capacity (no./year)'),
        containerTerminal: Yup.boolean().nullable(true),
        freightCapacityTnPy: createNumberValidation('Freight capacity (tonnes/year)'),
        roroFacilities: Yup.boolean().nullable(true),
        transhipFacilitiesIntermoTransp: Yup.boolean().nullable(true),
        railConnection: Yup.boolean().nullable(true),
        railConnectionTracks: createNumberValidation('Rail connection - no. of trucks'),
        roadConnection: Yup.boolean().nullable(true),
        roadConnectionLanes: createNumberValidation('Road connection – no. of lanes'),
        its: Yup.boolean().nullable(true),
        itsType: Yup.string('Value should be a text').nullable(true),  //I put here a string so it works correctly
        vtmis: Yup.boolean().nullable(true),
        intermodalUnitsHandling: Yup.boolean().nullable(true),
        accommodate740mTrains: Yup.boolean().nullable(true),
        receptionFacilities: Yup.boolean().nullable(true),
        degassingFacilities: Yup.boolean().nullable(true),
        noiseReductionMeasures: Yup.boolean().nullable(true),
        pollutionReductionMeasures: Yup.boolean().nullable(true),
        alternativeFuelsAvailability: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
    }),
    iwwPortsNpmMaintDTO: Yup.object().shape({
        maintCostTotalPy: createNumberValidation('Maintenance cost - total (Euro/year)'),
        maintCostLandslidePy: createNumberValidation('Maintenance cost - landside infrastructure (Euro/year)'),
        maintCostRiverslidePy: createNumberValidation('Maintenance Cost – maritime infrastructure (Euro/year)'),
        financeSource: Yup.string('Value should be a text').nullable(true),
        reqUpgradeIncCapPass: createNumberValidation('Requiring upgrade to increase capacity (freight)'),
        reqUpgradeIncCapFreight: createNumberValidation('TODIS'),
        remarks: Yup.string("remarks").nullable(true),
    }),
    iwwPortsNpmOpsDTO: Yup.object().shape({
        portTrafficPy: createNumberValidation('Port traffic (vessels/year)'),
        passTrafficPy: createNumberValidation('Passenger traffic (passengers/year)'),
        freightTrafficTonsPy: createNumberValidation('Freight traffic (tonnes/year)'),
        dangerGoodsTonsTurnover: createNumberValidation('Dangerous Goods turnover (tonnes/year)'),
        totalTurnover: createNumberValidation('Total turnover (kT/year)'),
        tonsLoaded: createNumberValidation('Tons loaded (kT/year)'),
        oilTons: createNumberValidation('Oil tonnes (kT/year)'),
        liquidBulkTons: createNumberValidation('Liquid bulk tonnes (kT/year)'),
        dryBulkTons: createNumberValidation('Dry bulk tonnes (kT/year)'),
        generalBulkTons: createNumberValidation('General bulk tonnes (kT/year)'),
        teuTons: createNumberValidation('TEU tonnes (kT/year)'),
        teusPy: createNumberValidation('TEU containers per year (no.)'),
        roro: createNumberValidation('RoRo (kT/year)'),
        rorosVeh: createNumberValidation('RoRos (no. of vehicles/year)'),
        storageCapacityUsed: createNumberValidation('Storage capacity used (% of capacity)'),
        transhipmentCapacityUsed: createNumberValidation('Transhipment capacity used (% of capacity)'),
        passengerCapacityUsed: createNumberValidation('Passenger capacity used (% of capacity)'),
        remarks: Yup.string("remarks").nullable(true),
    }),
    iwwPortsNpmTentcompDTO: Yup.object().shape({
        railConn: Yup.boolean().nullable(true),
        roadConn: Yup.boolean().nullable(true),
        cleanFuelsAvai: Yup.string('Value should be a string').nullable(true),
        terminalAvai: Yup.boolean().nullable(true),
        risDeploy: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
        intermodalUnitsHandling: Yup.string().nullable(true),
        accommodate740mTrains: Yup.boolean().nullable(true),
        environmentalPerformanceImprovementEquipment: Yup.boolean().nullable(true),
        alternativeFuelsAvailability: Yup.string().nullable(true),
        accommodate740mTrains: Yup.string().nullable(true),
        environmentalPerformanceImprovementEquipment: Yup.string().nullable(true),
    })

});




const NIWWPORTS_INIT = {
    //general section Rules:
    dateValidFor: "",
    tenTCategory: "",
    nodeNameval: "",
    nameResp: "",
    addressResp: "",
    contactPer: "",
    contactPos: "",
    contactNum: 0,
    contactEmail: "",
    countryCode: "",
    ownershipType: "",
    owner1: "",
    owner1Per: 0,
    ownerOther: "",
    ownerOtherPer: 0,
    remarks: "",
    iwwPortsNpmEnvDTO: {
        airPollutGhgTpy: "",
        co2Ems: "",
        noxEms: "",
        so2Ems: "",
        nmhcEms: "",
        ppm: "",
        noise: "",
        chFloodInci: "",
        chClosureAdvWeatherCond: "",
        chEmbankFail: "",
        chWinterMaintDays: "",
        remarks: ""
    },
    iwwPortsNpmInfraDTO: {
        activity: "",
        condition: "",
        totalArea: "",
        openStorage: "",
        coveredStorage: "",
        coldStorage: "",
        dangerGoodsStorage: "",
        handlingEquip: "",
        quayLength: "",
        berths: "",
        maxDraught: "",
        portTerminals: "",
        combinedTerminals: "",
        passengerTerminals: "",
        passengerCapacity: "",
        containerTerminal: false,
        freightCapacityTnPy: "",
        roroFacilities: false,
        transhipFacilitiesIntermoTransp: false,
        railConnection: false,
        railConnectionTracks: "",
        roadConnection: false,
        roadConnectionLanes: "",
        its: false,
        itsType: "",
        vtmis: false,
        intermodalUnitsHandling: false,
        accommodate740mTrains: false,
        receptionFacilities: false,
        degassingFacilities: false,
        noiseReductionMeasures: false,
        pollutionReductionMeasures: false,
        alternativeFuelsAvailability: false,
        remarks: ""
    },
    iwwPortsNpmMaintDTO: {
        maintCostTotalPy: "",
        maintCostLandslidePy: "",
        maintCostRiverslidePy: "",
        financeSource: "",
        reqUpgradeIncCapPass: "",
        reqUpgradeIncCapFreight: "",
        remarks: ""
    },
    iwwPortsNpmOpsDTO: {
        portTrafficPy: "",
        passTrafficPy: "",
        freightTrafficTonsPy: "",
        dangerGoodsTonsTurnover: "",
        totalTurnover: "",
        tonsLoaded: "",
        oilTons: "",
        liquidBulkTons: "",
        dryBulkTons: "",
        generalBulkTons: "",
        teuTons: "",
        teusPy: "",
        roro: "",
        rorosVeh: "",
        storageCapacityUsed: "",
        transhipmentCapacityUsed: "",
        passengerCapacityUsed: "",
        remarks: ""
    },
    iwwPortsNpmTentcompDTO: {
        railConn: false,
        roadConn: false,
        cleanFuelsAvai: false,
        terminalAvai: false,
        risDeploy: false,
        remarks: "",
        intermodalUnitsHandling: "",
        accommodate740mTrains: false,
        environmentalPerformanceImprovementEquipment: false,
        alternativeFuelsAvailability: "",
        accommodate740mTrains: "",
        environmentalPerformanceImprovementEquipment: "",
    }
}

export { TODIS_NIWWPORTS_SCHEMA, NIWWPORTS_INIT };